import { fetchAdministrationMarketplaceOpportunities, updateAdministrationMarketplaceOpportunityDescription, type AdministrationMarketplaceOpportunityQuery } from "../../../api/administrationMarketplace";
import { createKeyedResourceLoader } from "../../state/keyedResourceLoader";
import type { MarketplaceAdministrationSlice, MarketplaceOpportunityInventorySlice } from "./types";

const loader = createKeyedResourceLoader();
const emptySummary = { total: 0, available: 0, published: 0, unpublished: 0, reserved: 0, acquired: 0, needsReview: 0, acquiredRevenueTotals: [], acquiredGrossTotals: [], acquiredCreditTotals: [], acquiredAllowanceTotals: [], totalPublicPotentialTotals: [], totalSubscriberPotentialTotals: [], availablePublicPotentialTotals: [], availableSubscriberPotentialTotals: [], reservedPublicPotentialTotals: [], reservedSubscriberPotentialTotals: [], needsReviewPublicPotentialTotals: [], needsReviewSubscriberPotentialTotals: [] };

const normalizedFilter = (value: string | string[] | undefined) => {
  const values = Array.isArray(value) ? value : value && value !== "all" ? [value] : [];
  return values.length ? [...values].sort() : ["all"];
};

export const opportunityInventoryKey = (query: AdministrationMarketplaceOpportunityQuery) => JSON.stringify({
  productId: query.productId,
  policyId: query.policyId,
  tier: query.tier || "all",
  query: query.query?.trim() || "",
  publication: normalizedFilter(query.publication),
  publishability: normalizedFilter(query.publishability),
  inventoryState: normalizedFilter(query.inventoryState),
  view: query.view || "operational",
  dateFrom: query.dateFrom || "",
  dateTo: query.dateTo || "",
  sort: query.sort || "newest",
  limit: query.limit || 50,
});

export const marketplaceOpportunityInventoryInitialState = { opportunityInventories: {} };
export const resetMarketplaceOpportunityInventoryRequests = loader.clear;

export const createMarketplaceOpportunityInventorySlice:
MarketplaceAdministrationSlice<MarketplaceOpportunityInventorySlice> = (set, get) => ({
  ...marketplaceOpportunityInventoryInitialState,
  loadOpportunityInventory: (query, options = {}) => {
    const key = opportunityInventoryKey(query);
    const current = get().opportunityInventories[key];
    const append = Boolean(options.append && query.cursor);
    return loader.load({
      key: `${key}:${query.cursor || "first"}`,
      force: options.force,
      cached: !append && Boolean(current?.hydrated),
      request: () => fetchAdministrationMarketplaceOpportunities(query),
      onStart: () => set((state) => ({ opportunityInventories: { ...state.opportunityInventories, [key]: { query: { ...query, cursor: null }, items: current?.items || [], nextCursor: current?.nextCursor || null, totalCount: current?.totalCount || 0, financialSummary: current?.financialSummary || [], summary: current?.summary || emptySummary, scopeSummary: current?.scopeSummary || emptySummary, nextChangeAt: current?.nextChangeAt || null, hydrated: current?.hydrated || false, loading: !append, loadingMore: append, error: "" } } })),
      onSuccess: (page) => set((state) => ({ opportunityInventories: { ...state.opportunityInventories, [key]: { query: { ...query, cursor: null }, ...page, items: append ? [...(state.opportunityInventories[key]?.items || []), ...page.items.filter((item) => !(state.opportunityInventories[key]?.items || []).some((existing) => existing.id === item.id))] : page.items, hydrated: true, loading: false, loadingMore: false, error: "" } } })),
      onError: (error) => set((state) => ({ opportunityInventories: { ...state.opportunityInventories, [key]: { ...(state.opportunityInventories[key] || { query, items: [], nextCursor: null, totalCount: 0, financialSummary: [], summary: emptySummary, scopeSummary: emptySummary, nextChangeAt: null, hydrated: false }), loading: false, loadingMore: false, error } } })),
      onFinish: () => set((state) => state.opportunityInventories[key] ? ({ opportunityInventories: { ...state.opportunityInventories, [key]: { ...state.opportunityInventories[key], loading: false, loadingMore: false } } }) : state),
      errorMessage: "Unable to load Marketplace inventory.",
    });
  },
  refreshOpportunityInventories: () => Promise.all(Object.values(get().opportunityInventories).filter((entry) => entry.hydrated).map((entry) => get().loadOpportunityInventory({ ...entry.query, cursor: null }, { force: true }))).then(() => undefined),
  updateOpportunityDescription: async (opportunityId, description) => {
    const updated = await updateAdministrationMarketplaceOpportunityDescription(opportunityId, { description });
    set((state) => ({
      opportunityInventories: Object.fromEntries(Object.entries(state.opportunityInventories).map(([key, entry]) => [
        key,
        { ...entry, items: entry.items.map((item) => item.id === opportunityId ? updated : item) },
      ])),
    }));
    return updated;
  },
});
